<button
	type="button"
	class="agent-panel-backdrop"
	aria-label="Close FLAI"
	(click)="panelClose.emit()"
></button>

<aside
	#panel
	id="flying-agent-panel"
	class="agent-panel"
	[style.--agent-text-scale]="textSize / 100"
	[class.agent-panel-resizing]="isPanelResizing"
	role="complementary"
	aria-labelledby="agent-panel-title"
	tabindex="-1"
>
	<div
		class="agent-panel-resize-handle"
		role="separator"
		aria-label="Resize FLAI panel"
		aria-orientation="vertical"
		aria-controls="flying-agent-panel"
		[attr.aria-valuemin]="panelMinWidth"
		[attr.aria-valuemax]="panelMaxWidth"
		[attr.aria-valuenow]="panelWidth"
		[attr.aria-valuetext]="panelWidth + ' pixels wide'"
		tabindex="0"
		title="Drag to resize. Double-click to reset."
		(pointerdown)="startPanelResize($event)"
		(pointermove)="resizePanel($event)"
		(pointerup)="stopPanelResize($event)"
		(pointercancel)="stopPanelResize($event)"
		(lostpointercapture)="stopPanelResize()"
		(keydown)="onPanelResizeKeydown($event)"
		(dblclick)="resetPanelWidth()"
	></div>
	<header class="agent-panel-header">
		<div class="agent-panel-identity">
			<img
				class="agent-panel-mark"
				[src]="(selectedApp$ | async)?.logo"
				alt=""
			/>
			<div>
				<div class="agent-panel-title-row">
					<h2 id="agent-panel-title">FLAI</h2>
					<span class="agent-panel-beta">BETA</span>
				</div>
			</div>
		</div>

		<div class="agent-text-controls" role="group" aria-label="FLAI text size">
			<button
				type="button"
				aria-label="Decrease text size"
				title="Smaller text"
				[disabled]="textSize <= minTextSize"
				(click)="setTextSize(textSize - 10)"
			>A−</button>
			<button
				type="button"
				class="agent-text-reset"
				aria-label="Reset text size to 100 percent"
				title="Reset text size"
				(click)="setTextSize(100)"
			><span aria-live="polite" aria-atomic="true">{{ textSize }}%</span></button>
			<button
				type="button"
				aria-label="Increase text size"
				title="Larger text"
				[disabled]="textSize >= maxTextSize"
				(click)="setTextSize(textSize + 10)"
			>A+</button>
		</div>

		<button
			type="button"
			class="agent-panel-close"
			aria-label="Close FLAI"
			title="Close"
			(click)="panelClose.emit()"
		>
			<ng-fa-icon icon="xmark"></ng-fa-icon>
		</button>
	</header>

	<div
		#panelBody
		class="agent-panel-body scroll-bar"
		aria-live="polite"
	>
		<section
			*ngIf="messages.length === 0"
			class="agent-explain"
			aria-label="Explain this page"
		>
			<button
				type="button"
				class="agent-explain-action"
				[disabled]="isLoading"
				(click)="explainCurrentFunction()"
			>
				<span
					class="agent-action-icon"
					aria-hidden="true"
				>
					<ng-fa-icon icon="circle-question"></ng-fa-icon>
				</span>
				<span class="agent-action-copy">
					<strong>Explain this function</strong>
					<small>Get a simple explanation of the page you are viewing.</small>
				</span>
				<ng-fa-icon
					class="agent-action-arrow"
					icon="arrow-right"
				></ng-fa-icon>
			</button>
		</section>

		<section
			*ngIf="messages.length > 0 || isLoading || errorMessage"
			class="agent-conversation"
			aria-label="FLAI conversation"
		>
			<article
				*ngFor="let message of messages; trackBy: trackMessage"
				[hidden]="message.role === 'assistant' && !message.text"
				class="agent-message"
				[class.agent-message-user]="message.role === 'user'"
				[class.agent-message-assistant]="message.role === 'assistant'"
			>
				<span
					*ngIf="message.role === 'assistant'"
					class="agent-message-role"
				>
					FLAI
				</span>
				<small *ngIf="message.context">{{ message.context }}</small>
				<p *ngIf="message.role === 'user'">{{ message.text }}</p>
				<div
					*ngIf="message.role === 'assistant'"
					class="agent-message-markdown"
					[innerHTML]="renderMarkdown(message.text)"
					(click)="onMarkdownNavigation($event)"
				></div>
				<small
					*ngIf="message.role === 'assistant' && message.creditsUsed"
					class="agent-message-usage"
				>
					{{ message.creditsUsed | number }} account token used
				</small>
				<ol *ngIf="message.steps?.length">
					<li *ngFor="let step of message.steps">{{ step }}</li>
				</ol>
				<p
					*ngIf="message.tip"
					class="agent-message-tip"
				>
					<strong>Tip:</strong>
					{{ message.tip }}
				</p>
			</article>

			<div
				*ngIf="pendingAction"
				class="agent-action-confirmation"
				role="alertdialog"
				aria-labelledby="agent-action-confirmation-title"
				aria-describedby="agent-action-confirmation-description"
			>
				<strong id="agent-action-confirmation-title">{{ pendingAction.title }}</strong>
				<p id="agent-action-confirmation-description">{{ pendingAction.description }}</p>
				<div class="agent-action-confirmation-buttons">
					<button
						type="button"
						class="agent-action-cancel"
						(click)="cancelPendingAction()"
					>
						Cancel
					</button>
					<button
						type="button"
						class="agent-action-confirm"
						(click)="confirmPendingAction()"
					>
						Confirm
					</button>
				</div>
			</div>

			<p
				*ngIf="actionStatusMessage"
				class="agent-action-status"
				role="status"
			>
				{{ actionStatusMessage }}
			</p>

			<div
				*ngIf="isLoading && !hasStreamingResponse()"
				class="agent-message agent-message-assistant agent-message-loading"
				role="status"
			>
				<ng-fa-icon
					icon="spinner"
					animation="spin"
				></ng-fa-icon>
				<span>Working on your request...</span>
			</div>

			<div
				*ngIf="errorMessage"
				class="agent-error"
				role="alert"
			>
				<div class="agent-error-copy">
					<span>{{ errorMessage }}</span>
					<p *ngIf="canUpgradeDailyLimit">Upgrade to a subscription for more daily requests.</p>
				</div>
				<a
					*ngIf="canUpgradeDailyLimit"
					routerLink="/store"
					(click)="panelClose.emit()"
				>Upgrade</a>
				<button
					*ngIf="!isDailyLimitReached"
					type="button"
					[disabled]="isLoading"
					(click)="retryLastRequest()"
				>
					Retry
				</button>
			</div>
		</section>

		<section
			*ngIf="messages.length === 0"
			class="agent-actions"
			aria-labelledby="agent-actions-title"
		>
			<div class="agent-section-heading">
				<h3 id="agent-actions-title">Start an action</h3>
				<span>3 tools</span>
			</div>

			<a
				*ngFor="let action of actions"
				class="agent-action"
				[routerLink]="action.routerLink"
			>
				<span
					class="agent-action-icon"
					aria-hidden="true"
				>
					<ng-fa-icon [icon]="action.icon"></ng-fa-icon>
				</span>
				<span class="agent-action-copy">
					<strong>{{ action.title }}</strong>
					<small>{{ action.description }}</small>
				</span>
				<ng-fa-icon
					class="agent-action-arrow"
					icon="arrow-right"
				></ng-fa-icon>
			</a>
		</section>
	</div>

	<footer class="agent-panel-footer">
		<form
			class="agent-composer"
			(submit)="$event.preventDefault(); submitQuestion()"
		>
			<div
				*ngIf="messages.length > 0"
				class="agent-composer-heading"
			>
				<button
					type="button"
					class="agent-new-conversation"
					aria-label="Start a new conversation"
					title="Start a new conversation"
					(click)="startNewConversation()"
				>
					<ng-fa-icon icon="rotate-right"></ng-fa-icon>
					<span>New chat</span>
				</button>
				<button *ngIf="isLoading" type="button" class="btn btn-sm btn-outline-secondary" (click)="stopWorkflow()">Stop</button>
			</div>
			<div class="agent-composer-control">
				<textarea
					id="flying-agent-question"
					name="flyingAgentQuestion"
					aria-label="Ask FLAI"
					rows="1"
					maxlength="300"
					placeholder="Ask a question or tell FLAI what to do"
					aria-describedby="flying-agent-composer-hint"
					[(ngModel)]="question"
					[disabled]="isLoading"
					(keydown)="onComposerKeydown($event)"
				></textarea>
				<div class="agent-composer-toolbar">
					<span
						id="flying-agent-composer-hint"
						class="agent-composer-hint"
					>
						<kbd>Enter</kbd>
						to send ·
						<kbd>Shift</kbd>
						+
						<kbd>Enter</kbd>
						for new line
					</span>
					<div class="agent-composer-actions">
						<span class="agent-composer-count">{{ question.length }}/300</span>
						<button
							type="submit"
							class="agent-send"
							aria-label="Send message"
							title="Send message"
							[disabled]="!question.trim() || isLoading"
						>
							<ng-fa-icon icon="paper-plane"></ng-fa-icon>
						</button>
					</div>
				</div>
			</div>
		</form>
	</footer>
</aside>
